import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled, { keyframes } from 'styled-components';
import { FaBook, FaClock, FaTag } from 'react-icons/fa';
import { API } from '../config/api';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const BonusContentContainer = styled(motion.div)`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  padding: 6rem 2rem 4rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
    z-index: 1;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 4rem);
  color: #ffffff;
  font-weight: 900;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 300;
  letter-spacing: 1px;
`;

const ContentGrid = styled.div`
  display: grid;
  gap: 2rem;
  margin-top: 3rem;
`;

const ContentCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const ContentTitle = styled.h2`
  font-size: 1.8rem;
  color: #2d3436;
  margin-bottom: 1rem;
  font-weight: 700;
`;

const ContentMeta = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const MetaTag = styled.span`
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const ContentExcerpt = styled.p`
  color: #636e72;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 1rem;
`;

const ReadMoreButton = styled(motion.button)`
  background: linear-gradient(45deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
  }
`;

const FullContentModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: #636e72;
  
  &:hover {
    color: #2d3436;
  }
`;

const FullContentBody = styled.div`
  color: #2d3436;
  line-height: 1.8;
  font-size: 1.1rem;
  
  h1, h2, h3, h4, h5, h6 {
    color: #2d3436;
    margin: 1.5rem 0 1rem 0;
  }
  
  p {
    margin-bottom: 1rem;
  }
  
  blockquote {
    border-left: 4px solid #667eea;
    padding-left: 1rem;
    margin: 1.5rem 0;
    font-style: italic;
    color: #636e72;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  color: white;
  font-size: 1.2rem;
  padding: 4rem;
`;

const EmptyState = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  padding: 4rem;
`;

const BonusContent = () => {
  const [bonusContent, setBonusContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContent, setSelectedContent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchBonusContent();
  }, []);

  const fetchBonusContent = async () => {
    try {
      const response = await fetch(`${API}/bonus-content`);
      if (response.ok) {
        const data = await response.json();
        setBonusContent(data);
      } else {
        console.error('Failed to fetch bonus content');
      }
    } catch (error) {
      console.error('Error fetching bonus content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReadMore = (content) => {
    setSelectedContent(content);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedContent(null);
  };

  return (
    <BonusContentContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <ContentWrapper>
        <Header
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Title>Bonus Content</Title>
          <Subtitle>Exclusive writings and behind-the-scenes content</Subtitle>
        </Header>

        {loading ? (
          <LoadingState>Loading bonus content...</LoadingState>
        ) : bonusContent.length === 0 ? (
          <EmptyState>
            <h3>No bonus content available yet</h3>
            <p>Check back soon for exclusive writings and behind-the-scenes content!</p>
          </EmptyState>
        ) : (
          <ContentGrid>
            {bonusContent.map((content, index) => (
              <ContentCard
                key={content.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <ContentTitle>{content.title}</ContentTitle>
                <ContentMeta>
                  <MetaTag>{content.category || 'Writing'}</MetaTag>
                  <MetaTag>{new Date(content.createdAt).toLocaleDateString()}</MetaTag>
                  {content.readTime && <MetaTag>{content.readTime} min read</MetaTag>}
                </ContentMeta>
                <ContentExcerpt>
                  {content.excerpt || content.content.substring(0, 200) + '...'}
                </ContentExcerpt>
                <ReadMoreButton
                  onClick={() => handleReadMore(content)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Read Full Content
                </ReadMoreButton>
              </ContentCard>
            ))}
          </ContentGrid>
        )}
      </ContentWrapper>

      {/* Full Content Modal */}
      {showModal && selectedContent && (
        <FullContentModal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeModal}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton onClick={closeModal}>×</CloseButton>
            <ContentTitle>{selectedContent.title}</ContentTitle>
            <ContentMeta>
              <MetaTag>{selectedContent.category || 'Writing'}</MetaTag>
              <MetaTag>{new Date(selectedContent.createdAt).toLocaleDateString()}</MetaTag>
              {selectedContent.readTime && <MetaTag>{selectedContent.readTime} min read</MetaTag>}
            </ContentMeta>
            <FullContentBody 
              dangerouslySetInnerHTML={{ __html: selectedContent.content }}
            />
          </ModalContent>
        </FullContentModal>
      )}
    </BonusContentContainer>
  );
};

export default BonusContent;
